import styled from "styled-components";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  Th,
} from "@/components/Table";
import { SortDirection } from "@/utils/enums";
import { SortState, TestingCompetitionBonusResource } from "@/types/resources";
import Text from "@/components/Text";
import PendingBonusesTableRow from "./PendingBonusesTableRow";
import SearchBar from "@/components/SearchBar";
import { api } from "@/utils/api";
import useDebounce from "@/hooks/useDebounce";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "ended_at",
};

const StyledTable = styled(Table)`
  margin-bottom: 50px;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    font-weight: 700;
    &:nth-child(1) {
      width: calc(26% - 80px);
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 17%;
    }
    &:nth-child(3) {
      width: 13%;
    }
    &:nth-child(4) {
      width: 11%;
    }
    &:nth-child(5) {
      width: 14%;
    }
    &:nth-child(6) {
      width: 8%;
    }
    &:nth-child(7) {
      width: 8%;
    }
    &:last-child {
      width: 84px;
      position: relative;
      padding-right: 16px;
    }
  }
`;

const PendingBonusesTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: bonuses,
    isLoading,
    isError,
  } = useQuery<TestingCompetitionBonusResource[]>(
    [
      "bonuses",
      {
        type: "thumbnails",
        status: "pending",
        sort,
        ...(debouncedSearch.length > 2 && {
          search: debouncedSearch,
        }),
      },
    ],
    () =>
      api.get<never, TestingCompetitionBonusResource[]>(
        "/v1/gamification/thumbnails/bonuses",
        {
          params: {
            "filter[pending]": 1,
            ...(sort.direction !== "DEFAULT" && {
              sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
            }),
            ...(debouncedSearch.length > 2 && {
              "filter[search]": debouncedSearch,
            }),
          },
        }
      ),
    {
      staleTime: 1000 * 60,
    }
  );

  return (
    <>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by Project Name / Project ID / Participant Name"
      />
      <SectionHeading>Pending Bonuses</SectionHeading>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>
                Project Name
                <br />
                <Text as="span" $color="gray" $type="extraSmall">
                  Project ID
                </Text>
              </Th>
              <SortableTh
                property="team_name"
                title="Team"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="c_batch"
                title="Test Sequence"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="ended_at"
                title="Date Closed"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="user_name"
                title="Participant Name"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="ctr"
                title="CTR"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="reward"
                title="Bonus"
                setSort={setSort}
                sort={sort}
              />
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={8}
              isError={isError}
              hasData={!!bonuses?.length}
            >
              {bonuses?.map((bonus) => (
                <PendingBonusesTableRow key={bonus.id} bonus={bonus} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
    </>
  );
};

export default PendingBonusesTable;
